// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import {batchActions} from 'utils/batch_actions';

import {Client4} from 'mattermost-redux/client';
import {SearchTypes} from 'mattermost-redux/action_types';
import {getCurrentTeamId} from 'mattermost-redux/selectors/entities/teams';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/users';

import {type ActionResult, type DispatchFunc, type GetStateFunc, type ActionFunc} from 'mattermost-redux/types/actions';

import {type Post} from 'mattermost-redux/types/posts';

import {type FileSearchResults, type FileSearchResultItem} from 'mattermost-redux/types/files';
import type {Options} from 'packages/types/src/client4';

import {type SearchParameter} from 'mattermost-redux/types/search';

import {getChannelAndMyMember, getChannelMembers} from './channels';
import {forceLogoutIfNecessary} from './helpers';
import {getProfilesAndStatusesForPosts, receivedPosts} from './posts';
import {receivedFiles} from './files';
const WEBAPP_SEARCH_PER_PAGE = 20;
export function getMissingChannelsFromPosts(posts: Map<string, Post>): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const {
            channels,
            membersInChannel,
            myMembers,
        } = getState().entities.channels;
        const promises: Array<Promise<ActionResult>> = [];
        Object.values(posts).forEach((post) => {
            const id = post.channel_id;

            if (!channels[id] || !myMembers[id]) {
                promises.push(dispatch(getChannelAndMyMember(id)));
            }

            if (!membersInChannel[id]) {
                promises.push(dispatch(getChannelMembers(id)));
            }
        });
        return Promise.all(promises);
    };
}

export function getMissingChannelsFromFiles(files: Map<string, FileSearchResultItem>): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const {
            channels,
            membersInChannel,
            myMembers,
        } = getState().entities.channels;
        const promises: Array<Promise<ActionResult>> = [];
        Object.values(files).forEach((file) => {
            const id = file.channel_id;

            if (!channels[id] || !myMembers[id]) {
                promises.push(dispatch(getChannelAndMyMember(id)));
            }

            if (!membersInChannel[id]) {
                promises.push(dispatch(getChannelMembers(id)));
            }
        });
        return Promise.all(promises);
    };
}

export function searchPostsWithParams(teamId: string, params: SearchParameter): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const isGettingMore = params.page > 0;
        dispatch({
            type: SearchTypes.SEARCH_POSTS_REQUEST,
            isGettingMore,
        });

        try {
            const posts = await Client4.searchPostsWithParams(teamId, params)
                .then((posts) => Promise.all([
                    posts,
                    getProfilesAndStatusesForPosts(posts.posts, dispatch, getState) as any,
                    dispatch(getMissingChannelsFromPosts(posts.posts)) as any,
                ]))
                .then(([posts]) => posts);

            dispatch(batchActions([
                {
                    type: SearchTypes.RECEIVED_SEARCH_POSTS,
                    data: posts,
                    isGettingMore,
                },
                receivedPosts(posts),
                {
                    type: SearchTypes.RECEIVED_SEARCH_TERM,
                    data: {
                        teamId,
                        params,
                        isEnd: posts.order.length === 0,
                    },
                },
                {
                    type: SearchTypes.SEARCH_POSTS_SUCCESS,
                },
            ], 'SEARCH_POST_BATCH'));

            return {data: posts};
        } catch (error) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }
    };
}

export function searchPosts(teamId: string, terms: string, isOrSearch: boolean, includeDeletedChannels: boolean) {
    return searchPostsWithParams(teamId, {terms, is_or_search: isOrSearch, include_deleted_channels: includeDeletedChannels, page: 0, per_page: WEBAPP_SEARCH_PER_PAGE});
}

export function getMorePostsForSearch(): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const teamId = getCurrentTeamId(getState());
        const {params, isEnd} = getState().entities.search.current[teamId];
        if (!isEnd) {
            const newParams = Object.assign({}, params);
            newParams.page += 1;
            return dispatch(searchPostsWithParams(teamId, newParams));
        }
        return {data: true};
    };
}

export function clearSearch(): ActionFunc {
    return async (dispatch) => {
        dispatch({type: SearchTypes.REMOVE_SEARCH_POSTS});
        dispatch({type: SearchTypes.REMOVE_SEARCH_FILES});

        return {data: true};
    };
}

export function searchFilesWithParams(teamId: string, params: SearchParameter): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const isGettingMore = params.page > 0;
        dispatch({
            type: SearchTypes.SEARCH_FILES_REQUEST,
            isGettingMore,
        });

        let files: FileSearchResults;
        try {
            files = await Client4.searchFilesWithParams(teamId, params)
                .then((files) => Promise.all([
                    files,
                    dispatch(getMissingChannelsFromFiles(files.file_infos)),
                ]))
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .then(([files]) => files);
        } catch (error) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        dispatch(batchActions([
            {
                type: SearchTypes.RECEIVED_SEARCH_FILES,
                data: files,
                isGettingMore,
            },
            receivedFiles(files.file_infos),
            {
                type: SearchTypes.RECEIVED_SEARCH_TERM,
                data: {
                    teamId,
                    params,
                    isFilesEnd: files.order.length === 0,
                },
            },
            {
                type: SearchTypes.SEARCH_FILES_SUCCESS,
            },
        ], 'SEARCH_FILE_BATCH'));

        return {data: files};
    };
}

export function searchFiles(teamId: string, terms: string, isOrSearch: boolean, includeDeletedChannels: boolean) {
    return searchFilesWithParams(teamId, {terms, is_or_search: isOrSearch, include_deleted_channels: includeDeletedChannels, page: 0, per_page: WEBAPP_SEARCH_PER_PAGE});
}

export function getMoreFilesForSearch(): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const teamId = getCurrentTeamId(getState());
        const {params, isFilesEnd} = getState().entities.search.current[teamId];
        if (!isFilesEnd) {
            const newParams = Object.assign({}, params);
            newParams.page += 1;
            return dispatch(searchFilesWithParams(teamId, newParams));
        }
        return {data: true};
    };
}

export function getFlaggedPosts(page = 0, fetchOptions: Options = {}) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const userId = getCurrentUserId(state);

        let posts;
        try {
            posts = await Client4.getFlaggedPosts(
                userId,
                {
                    page,
                    perPage: 20,
                },
                fetchOptions,
            );

            await Promise.all([
                getProfilesAndStatusesForPosts(posts.posts, dispatch, getState),
                dispatch(getMissingChannelsFromPosts(posts.posts)),
            ]);
        } catch (error: unknown) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: SearchTypes.SEARCH_FLAGGED_POSTS_FAILURE, error});
            return {error};
        }

        dispatch({
            type: SearchTypes.RECEIVED_SEARCH_FLAGGED_POSTS,
            data: posts,
            isGettingMore: page > 0,
        });
        dispatch(receivedPosts(posts));
        return {data: posts};
    };
}

export function getPinnedPosts(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: SearchTypes.SEARCH_PINNED_POSTS_REQUEST});

        try {
            const result = await Client4.getPinnedPosts(channelId)
                .then((result) => Promise.all([
                    result,
                    getProfilesAndStatusesForPosts(result.posts, dispatch, getState),
                    dispatch(getMissingChannelsFromPosts(result.posts)),
                ]))
                .then(([result]) => result);

            dispatch(batchActions([
                {
                    type: SearchTypes.RECEIVED_SEARCH_PINNED_POSTS,
                    data: {
                        pinned: result,
                        channelId,
                    },
                },
                receivedPosts(result),
                {
                    type: SearchTypes.SEARCH_PINNED_POSTS_SUCCESS,
                },
            ], 'SEARCH_PINNED_POSTS_BATCH'));

            return {data: result};
        } catch (error) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: SearchTypes.SEARCH_PINNED_POSTS_FAILURE, error});
            return {error};
        }
    };
}

export function clearPinnedPosts(channelId: string): ActionFunc {
    return async (dispatch) => {
        dispatch({
            type: SearchTypes.REMOVE_SEARCH_PINNED_POSTS,
            data: {
                channelId,
            },
        });

        return {data: true};
    };
}

export function removeSearchTerms(teamId: string, terms: string): ActionFunc {
    return async (dispatch) => {
        dispatch({
            type: SearchTypes.REMOVE_SEARCH_TERM,
            data: {
                teamId,
                terms,
            },
        });

        return {data: true};
    };
}

export default {
    clearSearch,
    removeSearchTerms,
    searchPosts,
};
